export interface AttributeMappingItem {
  name: string;
  source: string;
}

export interface SpAttribute {
  name: string;
  source: string;
  label: string;
  type: string;
  required: boolean;
}

export interface SamlFormData {
  name: string;
  entity_id: string;
  acs_url: string;
  name_id_format: string;
  name_id_attribute: string;
  sign_assertion: boolean;
  require_signed_authn_requests: boolean;
  allow_idp_initiated: boolean;
  attribute_mapping: AttributeMappingItem[];
  attribute_release_policy: string[];
  attributes: SpAttribute[];
  is_active: boolean;
  customAppName?: string;
  metadata_file: string;
  metadata_url: string;
}

export interface SamlOption {
  value: string | boolean;
  label: string;
  uri?: string;
}

export interface SamlConfigurationOptions {
  common?: {
    signing_algorithm?: SamlOption[];
    digest_algorithm?: SamlOption[];
    sso_bindings?: SamlOption[];
  };

  service_provider?: {
    name_id_format?: SamlOption[];
    name_id_attribute?: SamlOption[];
    sign_assertion?: SamlOption[];
    require_signed_authn_requests?: SamlOption[];
    allow_idp_initiated?: SamlOption[];
    is_active?: SamlOption[];
  };

  fixed?: {
    protocol?: string;
    response_binding?: string;
    canonicalization_algorithm?: string;
    signed_authn_request_binding?: string;
    assertion_encryption?: boolean;
    single_logout?: boolean;
  };
}

export interface SamlCertificateAlert {
  status: string | null;
  severity: string | null;
  days_remaining: number | null;
  threshold_days: number | null;
  thresholds: number[];
  certificate_fingerprint_sha256: string | null;
  certificate_valid_from: string | null;
  certificate_valid_until: string | null;
  message: string | null;
}

export interface SamlIdp {
  service_provider_id?: number;
  service_provider_uuid?: string;
  service_provider_name?: string;
  entity_id: string;
  metadata_url: string;
  sso_url: string;
  idp_initiated_sso_url: string;
  signing_algorithm?: string;
  digest_algorithm?: string;
  sso_bindings?: string[];
  configuration_options?: SamlConfigurationOptions;
  certificate_alert?: SamlCertificateAlert;
  x509_certificate: string;
  name_id_format?: string;
  name_id_attribute?: string;
  certificate_download_url?: string;
}

export interface SamlServiceProvider {
  id: number;
  uuid: string;
  name: string;
  description: string | null;
  metadata_url: string | null;
  metadata_expires_in: string | null;
  metadata_refresh_hour: string | null;
  entity_id: string;
  acs_url: string;
  slo_url: string | null;
  has_x509_certificate: boolean;
  name_id_format: string;
  name_id_attribute: string;
  attribute_mapping: AttributeMappingItem[] | null;
  attribute_release_policy: string[];
  attribute_release_all_value?: string;
  attributes?: SpAttribute[];
  require_signed_authn_requests: boolean;
  sign_assertion: boolean;
  allow_idp_initiated: boolean;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
  idp?: SamlIdp;
  certificate_download_url: string | null;
  source: string | null;
}

export const defaultValues: SamlFormData = {
  name: '',
  entity_id: '',
  acs_url: '',
  name_id_format: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
  name_id_attribute: 'email',
  sign_assertion: true,
  require_signed_authn_requests: false,
  allow_idp_initiated: false,
  attribute_mapping: [{ name: 'email', source: 'email' }],
  attribute_release_policy: ['*'],
  attributes: [],
  is_active: true,
  metadata_file: '',
  metadata_url: '',
};

export interface FederationFormData {
  name: string;
  tenant_id: string;
  domain: string;
  azure_client_id: string;
  azure_client_secret: string;
  base_url: string;
  entity_id: string;
  source_url: string;
  metadata_valid_until: string;
  acs_endpoints: { Binding: string; Location: string; index: number; isDefault: boolean }[];
  slo_endpoints: { Binding: string; Location: string }[];
  name_id_formats: string[];
  certificates: { use: string; certificate: string }[];
  authn_requests_signed: boolean;
  want_assertions_signed: boolean;
  attribute_mapping: AttributeMappingItem[] | null;
  attribute_release_policy: string[];
  attribute_release_all_value?: string;
  attributes?: SpAttribute[];
}

export interface FederationData {
  id: number;
  name: string;
  azure_client_id: string | null;
  has_azure_client_secret: boolean;
  tenant_id: string | null;
  domain: string | null;
  federation_id: string | null;
  issuer_uri: string | null;
  signing_certificate: string | null;
  status: string;
  connected_at: string | null;
  owner_client_id: string | null;
  sso_url: string | null;
  logout_url: string | null;
  entity_id: string;
  acs_url: string | null;
  acs_endpoints: {
    index: string;
    Binding: string;
    Location: string;
  }[];

  slo_endpoints: {
    Binding: string;
    Location: string;
  }[];
  attribute_mapping: AttributeMappingItem[] | null;
  attribute_release_policy: string[];
  attribute_release_all_value?: string;
  attributes?: SpAttribute[];
  name_id_formats: string[];

  certificates: {
    use: string;
    certificate: string;
  }[];

  authn_requests_signed: boolean;
  want_assertions_signed: boolean;

  source_url: string | null;
  metadata_valid_until: string | null;
  imported_at: string | null;

  created_at: string;
  updated_at: string;

  is_registered: boolean;
  is_active: boolean;
  source: string | null;
  service_provider_uuid: string | null;
  source_type: string;
  source_file: string | null;
}
export const defaultValue: FederationFormData = {
  name: '',
  entity_id: '',
  tenant_id: '',
  domain: '',
  azure_client_id: '',
  azure_client_secret: '',
  base_url: '',
  source_url: '',
  metadata_valid_until: '',
  acs_endpoints: [{ Binding: '', Location: '', index: 0, isDefault: true }],
  slo_endpoints: [{ Binding: '', Location: '' }],
  name_id_formats: [''],
  certificates: [
    { use: 'signing', certificate: '' },
    { use: 'encryption', certificate: '' },
  ],
  authn_requests_signed: false,
  want_assertions_signed: true,
  attribute_mapping: [{ name: 'email', source: 'email' }],
  attribute_release_policy: ['*'],
  attributes: [],
};

export interface ProviderData {
  id?: string | number;
  type: string;
  idp_name: string;
  name: string;
  app_name: string;
  entity_id: string;
  acs_url: string;
  name_id_format: string;
  name_id_attribute: string;
  domain: string;
  base_url: string;
  protocol: string;
  signing_algorithm: string;
  digest_algorithm: string;
  sso_bindings: string[];
  x509_certificate: string;
  certificate_download_url: string;
  certificate_fingerprint_sha256: string;
  certificate_valid_from: string | null;
  certificate_valid_until: string | null;
  attributes?: SpAttribute[];
}

export interface IdpFormData {
  name: string;
  entity_id: string;
  sso_url: string;
  slo_url: string;
  x509_certificate: string;
  email_attribute: string;
  is_active: boolean;
  department_ids: number[];
  group_ids: number[];
  user_ids: number[];
}

export const idpDefaultValues: IdpFormData = {
  name: '',
  entity_id: '',
  sso_url: '',
  slo_url: '',
  x509_certificate: '',
  email_attribute: 'email',
  is_active: true,
  department_ids: [],
  group_ids: [],
  user_ids: []
};

export interface IdpRecord {
  id: number;
  uuid: string;
  name: string;
  entity_id: string;
  sso_url: string;
  slo_url: string | null;
  email_attribute: string | null;
  is_active: boolean;
  certificate_fingerprint_sha256: string;
  sp: {
    entity_id: string;
    metadata_url: string;
    acs_url: string;
    login_url: string;
  };
  department_ids:  number[];
  group_ids:  number[];
  user_ids: number[];
}

export interface IdentityProviderData {
  id?: string | number;
  idp_name: string;
  protocol: string;
  signing_algorithm: string;
  digest_algorithm: string;
  sso_bindings: string[];
  configuration_options?: SamlConfigurationOptions;
  certificate_alert?: SamlCertificateAlert;
  x509_certificate: string;
  certificate_download_url: string;
  certificate_fingerprint_sha256: string;
  certificate_valid_from: string | null;
  certificate_valid_until: string | null;
  entity_id: string;
  metadata_url: string;
  acs_url: string;
  login_url: string;
  sso_url: string;

}

export interface EndUseSamlData {
  id?: string | number;
  app_name: string;
  login_url: string;
  provider: string;
  is_connected: boolean;
  digest_algorithm: string;
  sso_bindings: string[];
  configuration_options?: SamlConfigurationOptions;
  certificate_alert?: SamlCertificateAlert;
  x509_certificate: string;
  certificate_download_url: string;
  certificate_fingerprint_sha256: string;
  certificate_valid_from: string | null;
  certificate_valid_until: string | null;
}
